import {
	createRootRouteWithContext,
	type ErrorComponentProps,
	HeadContent,
	Scripts,
	useNavigate,
	useRouter
} from '@tanstack/react-router'
import type { ReactNode } from 'react'
import type { RouterContext } from '@/router'
import { Button } from '@repo/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from '@repo/ui/card'
import { ThemeProvider } from '@repo/ui/commons/theme-provider'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import '@/index.css'
import '@/lib/i18n'
import { useTranslation } from '@/lib/i18n'

export const Route = createRootRouteWithContext<RouterContext>()({
	ssr: false,
	head: () => ({
		meta: [
			{ charSet: 'utf-8' },
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1.0'
			},
			{ title: 'Seal Ledger Backoffice' }
		]
	}),
	shellComponent: RootDocument
})

function RootDocument({ children }: { children: ReactNode }) {
	return (
		<html lang='en'>
			<head>
				<HeadContent />
			</head>
			<body>
				<ThemeProvider>{children}</ThemeProvider>
				<Scripts />
			</body>
		</html>
	)
}

export function BackofficeRouteError(props: ErrorComponentProps) {
	const router = useRouter()
	const { t } = useTranslation()

	return (
		<FallbackPage
			actions={
				<Button
					onClick={async () => {
						props.reset()
						await router.invalidate()
					}}
				>
					<RefreshCw />
					{t('common:retry')}
				</Button>
			}
			description={t('common:unexpected_error_copy')}
			title={t('common:unexpected_error')}
		/>
	)
}

export function BackofficeNotFound() {
	const navigate = useNavigate()
	const { t } = useTranslation()

	return (
		<FallbackPage
			actions={
				<Button onClick={() => navigate({ to: '/' })}>
					{t('common:go_overview')}
				</Button>
			}
			description={t('common:not_found_copy')}
			title={t('common:not_found')}
		/>
	)
}

function FallbackPage(props: {
	actions: ReactNode
	description: string
	title: string
}) {
	return (
		<main className='grid min-h-svh place-items-center p-6'>
			<Card className='w-full max-w-xl rounded-2xl'>
				<CardHeader className='gap-5'>
					<div className='grid size-12 place-items-center rounded-xl bg-destructive/10 text-destructive'>
						<TriangleAlert className='size-6' />
					</div>
					<CardTitle className='text-2xl'>{props.title}</CardTitle>
					<CardDescription className='text-base'>
						{props.description}
					</CardDescription>
				</CardHeader>
				<CardContent>{props.actions}</CardContent>
			</Card>
		</main>
	)
}
